<script setup>
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

import { api } from '@/api/client.js';
import PanelCard from '@/components/panels/PanelCard.vue';
import PulseCounter from '@/components/panels/PulseCounter.vue';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';
import { PULSE_EMPTY } from '@/utils/emptyStateCopy.js';

const tenant = useTenantStore();
const { activeAppId } = storeToRefs(tenant);
const ps = usePanelState('loading');
const data = ref({ count: 0, sparkline: [] });

async function fetchFn({ signal }) {
  if (!activeAppId.value) return;
  return api.events.pulse({ appId: activeAppId.value, signal });
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onSuccess(result) {
    if (!result || result.ok !== true) return;
    data.value = { count: result.count, sparkline: result.sparkline };
    if (result.count === 0 && (!result.sparkline || result.sparkline.length === 0)) {
      ps.setState('empty');
    } else {
      ps.setState('data');
    }
  },
  onError(err) {
    ps.setState('error', {
      message: "Couldn't load active sessions",
      detail: err?.message ?? '',
    });
  },
});

ps.setRetryHandler(() => tick());

defineExpose({ ps, refreshStatus, lastUpdatedAt });
</script>

<template>
  <PanelCard
    title="Active sessions"
    help-key="activeSessions"
    :state="ps.state.value"
    :empty-headline="PULSE_EMPTY.headline"
    :empty-body="PULSE_EMPTY.body"
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <p class="pulse-period">
      Count: last 5 minutes. Trend: last 60 minutes.
    </p>
    <PulseCounter
      :value="data.count"
      :sparkline-points="data.sparkline"
      size="hero"
    />
    <template
      v-if="data.sparkline.length"
      #tableFallback
    >
      <table class="pulse-table">
        <caption class="visually-hidden">
          Active sessions per minute (last 60 min)
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Minute bucket
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Active sessions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(b, i) in data.sparkline"
            :key="`p-${i}`"
          >
            <td>{{ b.ts }}</td>
            <td class="numeric">
              {{ b.value }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.pulse-period {
  margin: 0 0 var(--space-2);
  font-size: var(--text-xs);
  color: var(--fg-secondary);
}

.pulse-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.pulse-table th,
.pulse-table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
}

.pulse-table th {
  color: var(--fg-secondary);
  font-weight: var(--fw-medium);
}

.pulse-table .numeric {
  text-align: right;
  font-feature-settings: "tnum";
}
</style>
